import React, { useState, useEffect } from 'react';
import { 
  Box, 
  VStack, 
  Heading, 
  Text, 
  Button, 
  Image, 
  SimpleGrid, 
  useToast,
  Divider,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCrown, FaCheck, FaArrowLeft } from 'react-icons/fa';
import { supabase } from '../services/supabaseClient';
import LogoBarber from '../assets/Logo-Barber.png';

const MotionBox = motion(Box);

const BecomeMemberPage = ({ userData, onBack }) => {
  const [pacotes, setPacotes] = useState([]);
  const [servicosPorPacote, setServicosPorPacote] = useState({});
  const [selectedPacote, setSelectedPacote] = useState(null);
  const [loading, setLoading] = useState(true);
  // Simplificado para o totem - usamos apenas os dados do check-in
  const [formData, setFormData] = useState({
    nome: userData?.name || '',
    telefone: userData?.phone || '',
    pacoteId: ''
  });
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Buscar pacotes disponíveis e seus serviços
  useEffect(() => {
    const fetchPacotesEServicos = async () => {
      try {
        // Buscar pacotes
        const { data: pacotesData, error: pacotesError } = await supabase
          .from('pacotes_mensalistas')
          .select('*')
          .order('valor_mensal', { ascending: true });
        
        if (pacotesError) throw pacotesError;
        
        setPacotes(pacotesData || []);
        
        // Para cada pacote, buscar os serviços inclusos
        const servicosPorPacoteObj = {};
        
        for (const pacote of pacotesData || []) {
          const { data: servicosData, error: servicosError } = await supabase
            .from('pacote_servicos')
            .select(`
              servico_id,
              quantidade_inclusa,
              servicos(id, nome)
            `)
            .eq('pacote_id', pacote.id);
            
          if (!servicosError) {
            servicosPorPacoteObj[pacote.id] = servicosData || [];
          }
        }
        
        setServicosPorPacote(servicosPorPacoteObj);
      } catch (error) {
        console.error('Erro ao buscar pacotes:', error);
        toast({
          title: 'Erro ao carregar pacotes',
          description: 'Não foi possível carregar os pacotes disponíveis.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPacotesEServicos();
  }, [toast]);

  // Selecionar um pacote
  const handleSelectPacote = (pacote) => {
    setSelectedPacote(pacote);
    setFormData({
      ...formData,
      pacoteId: pacote.id
    });
    onOpen(); // Abrir modal de formulário
  };



  // Enviar solicitação de associação
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Verificar se já existe um mensalista com este telefone
      const phoneDigits = userData?.phone?.replace(/\D/g, '') || '';
      
      if (!phoneDigits) {
        toast({
          title: 'Telefone não encontrado',
          description: 'Não foi possível identificar seu telefone. Por favor, faça check-in novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }
      
      const { data: existingMensalista, error: checkError } = await supabase
        .from('mensalistas')
        .select('id')
        .eq('telefone', phoneDigits)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }
      
      if (existingMensalista) {
        toast({
          title: 'Telefone já cadastrado',
          description: 'Já existe um mensalista cadastrado com este telefone.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }
      
      // Obter data atual e data de fim (30 dias depois)
      const hoje = new Date();
      const dataFim = new Date();
      dataFim.setDate(hoje.getDate() + 30);
      
      // Cadastrar novo mensalista
      const { error: insertError } = await supabase
        .from('mensalistas')
        .insert([
          {
            nome: userData?.name || 'Cliente',
            telefone: phoneDigits,
            status: 'pendente', // Status inicial pendente até confirmação do pagamento
            pacote_id: selectedPacote.id,
            data_inicio: hoje.toISOString().split('T')[0],
            data_fim: dataFim.toISOString().split('T')[0],
            created_at: hoje.toISOString()
          }
        ])
        .select()
        .single();
      
      if (insertError) throw insertError;
      
      toast({
        title: 'Solicitação enviada com sucesso!',
        description: 'Sua solicitação para se tornar sócio foi enviada. Um de nossos atendentes entrará em contato para confirmar o pagamento.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Fechar modal e voltar para o menu
      onClose();
      setTimeout(() => {
        onBack();
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao cadastrar mensalista:', error);
      toast({
        title: 'Erro ao enviar solicitação',
        description: 'Ocorreu um erro ao enviar sua solicitação. Por favor, tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Formatar valor para exibição
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      padding={6}
      overflowY="scroll"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        '-ms-overflow-style': 'none',
      }}
    >
      <VStack spacing={6} width="100%" maxW="1200px" mx="auto">
        {/* Cabeçalho */}
        <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
          <Button 
            leftIcon={<FaArrowLeft />} 
            variant="ghost" 
            colorScheme="whiteAlpha" 
            onClick={onBack}
          >
            Voltar
          </Button>
          <Image 
            src={LogoBarber} 
            alt="Logo Barbearia" 
            height="80px" 
            objectFit="contain" 
          />
        </Box>

        {/* Título */}
        <VStack spacing={2} mb={8}>
          <HStack>
            <FaCrown size={32} color="#ECC94B" />
            <Heading color="yellow.400" fontSize="3xl" textAlign="center">
              Torne-se Sócio
            </Heading>
          </HStack>
          <Text color="whiteAlpha.800" textAlign="center" maxW="800px">
            Escolha um de nossos planos exclusivos e aproveite vantagens especiais, 
            como descontos em serviços, produtos e muito mais!
          </Text>
        </VStack>

        {/* Lista de pacotes */}
        {loading ? (
          <Text color="white">Carregando pacotes disponíveis...</Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} width="100%" alignItems="stretch">
            {pacotes.map((pacote) => (
              <Box
                key={pacote.id}
                bg="gray.800"
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                transition="all 0.3s"
                _hover={{ transform: 'translateY(-5px)', boxShadow: 'xl' }}
                position="relative"
                height="100%"
                display="flex"
                flexDirection="column"
              >
                {pacote.destaque && (
                  <Badge
                    position="absolute"
                    top={4}
                    right={4}
                    colorScheme="yellow"
                    fontSize="sm"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    Mais Popular
                  </Badge>
                )}
                
                <Box 
                  bg={pacote.destaque ? "yellow.500" : "gray.700"} 
                  color={pacote.destaque ? "black" : "white"}
                  py={4}
                  px={6}
                >
                  <Heading size="lg" fontWeight="bold">
                    {pacote.nome}
                  </Heading>
                </Box>
                
                <VStack p={6} spacing={4} align="stretch" flex="1">
                  <Heading size="2xl" color="white" textAlign="center">
                    {formatCurrency(pacote.valor_mensal)}
                    <Text as="span" fontSize="md" color="whiteAlpha.700">/mês</Text>
                  </Heading>
                  
                  <Divider />
                  
                  <Box flex="1" mb={16}> {/* Adicionado margem inferior para espaço do botão */}
                    <VStack spacing={3} align="start">
                      {pacote.descricao && (
                        <Text color="whiteAlpha.800">{pacote.descricao}</Text>
                      )}
                      
                      {pacote.beneficios && pacote.beneficios.split('\n').map((beneficio, index) => (
                        <HStack key={index} spacing={2} align="start">
                          <Box color="green.400" mt={1}>
                            <FaCheck />
                          </Box>
                          <Text color="whiteAlpha.900">{beneficio}</Text>
                        </HStack>
                      ))}

                      {/* Serviços inclusos no pacote */}
                      {servicosPorPacote[pacote.id] && servicosPorPacote[pacote.id].length > 0 && (
                        <>
                          <Text color="yellow.400" fontWeight="bold" mt={2}>
                            Serviços inclusos:
                          </Text>
                          {servicosPorPacote[pacote.id].map((servicoItem) => (
                            <HStack key={servicoItem.servico_id} spacing={2} align="start">
                              <Box color="yellow.400" mt={1}>
                                <FaCheck />
                              </Box>
                              <Text color="whiteAlpha.900">
                                {servicoItem.servicos?.nome} 
                                {servicoItem.quantidade_inclusa > 1 && (
                                  <Badge ml={1} colorScheme="yellow" fontSize="xs">
                                    {servicoItem.quantidade_inclusa}x/mês
                                  </Badge>
                                )}
                              </Text>
                            </HStack>
                          ))}
                        </>
                      )}
                    </VStack>
                  </Box>
                  
                  <Box position="absolute" bottom={6} left={6} right={6}>
                    <Button
                      colorScheme={pacote.destaque ? "yellow" : "blue"}
                      size="lg"
                      width="100%"
                      onClick={() => handleSelectPacote(pacote)}
                    >
                      Selecionar Plano
                    </Button>
                  </Box>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </VStack>

      {/* Modal de confirmação simplificado para o totem */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>
            <HStack>
              <FaCrown color="#ECC94B" />
              <Text>Confirmação de Plano</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="yellow.500" bg="blackAlpha.300">
                <Text fontSize="lg" mb={2}>
                  Você selecionou o plano:
                </Text>
                <Heading size="lg" color="yellow.400" mb={3}>
                  {selectedPacote?.nome}
                </Heading>
                <Text fontSize="2xl" fontWeight="bold" mb={3}>
                  {formatCurrency(selectedPacote?.valor_mensal)}<Text as="span" fontSize="md" color="whiteAlpha.700">/mês</Text>
                </Text>
                
                {/* Serviços inclusos no modal de confirmação */}
                {selectedPacote && servicosPorPacote[selectedPacote.id] && servicosPorPacote[selectedPacote.id].length > 0 && (
                  <Box mt={2}>
                    <Text color="yellow.400" fontWeight="bold" mb={2}>
                      Serviços inclusos no plano:
                    </Text>
                    {servicosPorPacote[selectedPacote.id].map((servicoItem) => (
                      <HStack key={servicoItem.servico_id} spacing={2} align="start" mb={1}>
                        <Box color="yellow.400" mt={1}>
                          <FaCheck size={12} />
                        </Box>
                        <Text color="whiteAlpha.900" fontSize="sm">
                          {servicoItem.servicos?.nome} 
                          {servicoItem.quantidade_inclusa > 1 && (
                            <Badge ml={1} colorScheme="yellow" fontSize="xs">
                              {servicoItem.quantidade_inclusa}x/mês
                            </Badge>
                          )}
                        </Text>
                      </HStack>
                    ))}
                  </Box>
                )}
              </Box>
              
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="whiteAlpha.300" bg="blackAlpha.300">
                <Text fontSize="md" mb={3}>
                  Seus dados para cadastro:
                </Text>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">Nome:</Text>
                  <Text>{userData?.name || 'Não informado'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Telefone:</Text>
                  <Text>{userData?.phone || 'Não informado'}</Text>
                </HStack>
              </Box>
              
              <Text fontSize="sm" color="whiteAlpha.700" textAlign="center">
                Ao confirmar, sua solicitação será enviada e um de nossos atendentes entrará em contato para confirmar o pagamento e finalizar seu cadastro.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              colorScheme="yellow" 
              onClick={handleSubmit} 
              isLoading={loading}
              leftIcon={<FaCrown />}
              size="lg"
            >
              Confirmar Solicitação
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
};

export default BecomeMemberPage;
